import React, { useState, useEffect } from 'react';
import { BatchIngredientWithSupplier } from '../../../types/batchIngredientWithSupplier';
import { useAppSelector } from '../../../store/hooks';
import { ingredientTraceCodeApiService } from '../../../services/ingredientTraceCodeApiService';
import { LotNumberOption } from '../../../types/componentRecord';
import { FillingBatchComponentBatch } from '../../../types/fillingBatchComponentBatch';
import { BatchRecordFillingBatch } from '../../../types/batchRecordFillingBatch';
import { BatchRecordDoughBatch } from '../../../types/batchRecordDoughBatch';

interface BatchIngredientDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  batchLotNumber: string;
  productName: string;
  dateOfProduction: string;
  ingredients: BatchIngredientWithSupplier[];
  isLoading: boolean;
  batchType: 'component' | 'filling' | 'dough' | 'product';
  batchId: string;
  onIngredientAdded: () => void;
  onDuplicate: () => void;
}

export const BatchIngredientDetailsModal: React.FC<BatchIngredientDetailsModalProps> = ({
  isOpen,
  onClose,
  batchLotNumber,
  productName,
  dateOfProduction,
  ingredients,
  isLoading,
  batchType,
  batchId,
  onIngredientAdded,
  onDuplicate
}) => {
  // Add-lot inline selector state
  interface AddLotState {
    isOpen: boolean;
    ingredientId: string | null;
    ingredientName: string;
    availableLots: LotNumberOption[];
    selectedLots: string[];
    loading: boolean;
    saving: boolean;
    error: string | null;
  }

  const [addLotState, setAddLotState] = useState<AddLotState>({
    isOpen: false,
    ingredientId: null,
    ingredientName: '',
    availableLots: [],
    selectedLots: [],
    loading: false,
    saving: false,
    error: null
  });

  // Add-ingredient form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [addIngredientName, setAddIngredientName] = useState('');
  const [selectedLotNumber, setSelectedLotNumber] = useState<string | null>(null);
  const [addQuantity, setAddQuantity] = useState('');
  const [addUnit, setAddUnit] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Redux data for ingredient/lot resolution
  const traceCodes = useAppSelector(state => state.ingredientTraceCodes.traceCodes);
  const rawIngredients = useAppSelector(state => state.rawIngredients.ingredients);
  const componentBatches = useAppSelector(state => state.componentBatches.items);
  const fillingBatches = useAppSelector(state => state.fillingBatches.items);
  const doughBatches = useAppSelector(state => state.doughBatches.items);

  // Phase 2: linked component batches state
  const [linkedComponentBatches, setLinkedComponentBatches] = useState<FillingBatchComponentBatch[]>([]);
  const [linkedFillingBatches, setLinkedFillingBatches] = useState<BatchRecordFillingBatch[]>([]);
  const [linkedDoughBatches, setLinkedDoughBatches] = useState<BatchRecordDoughBatch[]>([]);
  const [junctionLoading, setJunctionLoading] = useState(false);

  interface AddSubBatchState {
    isOpen: boolean;
    targetType: 'componentBatch' | 'fillingBatch' | 'doughBatch';
    groupName: string;
    availableBatches: { id: string; lotNumber: string; date?: string }[];
    selectedIds: string[];
    saving: boolean;
    error: string | null;
  }

  const [addSubBatchState, setAddSubBatchState] = useState<AddSubBatchState>({
    isOpen: false,
    targetType: 'componentBatch',
    groupName: '',
    availableBatches: [],
    selectedIds: [],
    saving: false,
    error: null
  });

  // Reset form state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowAddForm(false);
      setAddIngredientName('');
      setSelectedLotNumber(null);
      setAddQuantity('');
      setAddUnit('');
      setAddLoading(false);
      setAddError(null);
      setAddLotState({ isOpen: false, ingredientId: null, ingredientName: '', availableLots: [], selectedLots: [], loading: false, saving: false, error: null });
      setLinkedComponentBatches([]);
      setLinkedFillingBatches([]);
      setLinkedDoughBatches([]);
      setJunctionLoading(false);
      setAddSubBatchState({ isOpen: false, targetType: 'componentBatch', groupName: '', availableBatches: [], selectedIds: [], saving: false, error: null });
    }
  }, [isOpen]);

  // Reset lot selection when ingredient name changes
  useEffect(() => {
    setSelectedLotNumber(null);
  }, [addIngredientName]);

  const getUniqueIngredientNames = (): string[] => {
    const names = new Set(rawIngredients.map(ing => ing.name));
    return Array.from(names).sort();
  };

  const getAvailableLots = (): string[] => {
    if (!addIngredientName) return [];
    const matchingIngredients = rawIngredients.filter(ing => ing.name === addIngredientName);
    if (matchingIngredients.length === 0) return [];
    const receivingLogIds = matchingIngredients.map(ing => ing.receivingLogId);
    const matchingTraceCodes = traceCodes.filter(
      tc => receivingLogIds.includes(tc.ingredientReceivingId || '')
    );
    return Array.from(new Set(matchingTraceCodes.map(tc => tc.internalTraceCode)));
  };

  const getRawIngredientIdForLot = (lotNumber: string): string | null => {
    const traceCode = traceCodes.find(tc => tc.internalTraceCode === lotNumber);
    if (!traceCode) return null;
    const rawIngredient = rawIngredients.find(
      ing => ing.receivingLogId === traceCode.ingredientReceivingId
    );
    return rawIngredient?.id || null;
  };

  const handleIngredientNameChange = (newName: string) => {
    setAddIngredientName(newName);
    // Auto-populate quantity & unit from existing batch ingredient data
    const existingMatch = ingredients.find(i => i.ingredientName === newName);
    if (existingMatch) {
      setAddQuantity(existingMatch.quantity != null ? existingMatch.quantity.toString() : '');
      setAddUnit(existingMatch.unit || '');
    } else {
      setAddQuantity('');
      setAddUnit('');
    }
  };

  const handleAddIngredient = async () => {
    if (!addIngredientName || !selectedLotNumber) {
      setAddError('Please select an ingredient and a lot number.');
      return;
    }

    const rawIngredientId = getRawIngredientIdForLot(selectedLotNumber);
    if (!rawIngredientId) {
      setAddError('Could not resolve ingredient ID for the selected lot.');
      return;
    }

    const quantityNum = addQuantity ? parseFloat(addQuantity) : undefined;
    if (addQuantity && (isNaN(quantityNum!) || quantityNum! <= 0)) {
      setAddError('Quantity must be a positive number.');
      return;
    }

    setAddLoading(true);
    setAddError(null);

    try {
      let endpoint = '';
      let body: Record<string, unknown> = {};

      if (batchType === 'component') {
        endpoint = '/api/component-batch-ingredients';
        body = {
          componentBatchId: batchId,
          rawIngredientId,
          lotNumbers: [selectedLotNumber],
          quantity: quantityNum,
          unit: addUnit || undefined
        };
      } else if (batchType === 'filling') {
        endpoint = '/api/filling-batch-ingredients';
        body = {
          fillingBatchId: batchId,
          rawIngredientId,
          lotNumbers: [selectedLotNumber],
          quantity: quantityNum,
          unit: addUnit || undefined
        };
      } else if (batchType === 'dough') {
        endpoint = '/api/dough-batch-ingredients';
        body = {
          doughBatchId: batchId,
          rawIngredientId,
          ingredientName: addIngredientName,
          lotNumber: selectedLotNumber,
          quantity: quantityNum,
          unit: addUnit || undefined
        };
      } else {
        // product
        endpoint = '/api/batch-record-ingredients';
        body = {
          batchRecordId: batchId,
          rawIngredientId,
          lotNumbers: [selectedLotNumber],
          quantity: quantityNum,
          unit: addUnit || undefined
        };
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Failed to add ingredient (${response.status})`);
      }

      // Success: reset form and refresh parent
      setShowAddForm(false);
      setAddIngredientName('');
      setSelectedLotNumber(null);
      setAddQuantity('');
      setAddUnit('');
      onIngredientAdded();
    } catch (err: unknown) {
      setAddError(err instanceof Error ? err.message : 'Failed to add ingredient.');
    } finally {
      setAddLoading(false);
    }
  };

  const handleOpenAddLot = async (ingredient: BatchIngredientWithSupplier) => {
    setAddLotState({
      isOpen: true,
      ingredientId: ingredient.id,
      ingredientName: ingredient.ingredientName,
      availableLots: [],
      selectedLots: [],
      loading: true,
      saving: false,
      error: null
    });

    try {
      const allLots: LotNumberOption[] = await ingredientTraceCodeApiService.fetchTraceCodesByIngredientName(ingredient.ingredientName);
      const existingLots = new Set(ingredient.lotNumbers);
      const available = allLots.filter(lot => !existingLots.has(lot.lotNumber));
      setAddLotState(prev => ({ ...prev, availableLots: available, loading: false }));
    } catch {
      setAddLotState(prev => ({ ...prev, loading: false, error: 'Failed to load available lots.' }));
    }
  };

  const handleConfirmAddLots = async () => {
    if (!addLotState.ingredientId || addLotState.selectedLots.length === 0) return;

    setAddLotState(prev => ({ ...prev, saving: true, error: null }));

    const currentIngredient = ingredients.find(i => i.id === addLotState.ingredientId);
    if (!currentIngredient) return;

    const mergedLots = [...currentIngredient.lotNumbers, ...addLotState.selectedLots];

    let endpoint = '';
    if (batchType === 'component') {
      endpoint = `/api/component-batch-ingredients/${addLotState.ingredientId}`;
    } else if (batchType === 'filling') {
      endpoint = `/api/filling-batch-ingredients/${addLotState.ingredientId}`;
    } else if (batchType === 'product') {
      endpoint = `/api/batch-record-ingredients/${addLotState.ingredientId}`;
    }

    try {
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lotNumbers: mergedLots })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || errorData?.message || `Failed to update lots (${response.status})`);
      }

      setAddLotState({ isOpen: false, ingredientId: null, ingredientName: '', availableLots: [], selectedLots: [], loading: false, saving: false, error: null });
      onIngredientAdded();
    } catch (err: unknown) {
      setAddLotState(prev => ({
        ...prev,
        saving: false,
        error: err instanceof Error ? err.message : 'Failed to add lots.'
      }));
    }
  };

  // Phase 2: Fetch junction data on modal open
  useEffect(() => {
    if (!isOpen || !batchId) return;

    const fetchJunctionData = async () => {
      setJunctionLoading(true);
      try {
        if (batchType === 'filling') {
          const res = await fetch(`/api/filling-batch-component-batches/by-filling-batch/${batchId}`);
          if (res.ok) {
            const data = await res.json();
            setLinkedComponentBatches(data.fillingBatchComponentBatches || []);
          }
        }
        if (batchType === 'product') {
          const fillingRes = await fetch(`/api/batch-record-filling-batches/by-batch-record/${batchId}`);
          if (fillingRes.ok) {
            const data = await fillingRes.json();
            setLinkedFillingBatches(data.batchRecordFillingBatches || []);
          }
          const doughRes = await fetch(`/api/batch-record-dough-batches/by-batch-record/${batchId}`);
          if (doughRes.ok) {
            const data = await doughRes.json();
            setLinkedDoughBatches(data.batchRecordDoughBatches || []);
          }
        }
      } catch {
        // silent fail — sections just won't render
      } finally {
        setJunctionLoading(false);
      }
    };

    fetchJunctionData();
  }, [isOpen, batchId, batchType]);

  // Phase 2: Group linked component batches by name
  const groupedComponentBatches = linkedComponentBatches.reduce<Record<string, FillingBatchComponentBatch[]>>((acc, cb) => {
    const name = cb.componentName || 'Unknown';
    if (!acc[name]) acc[name] = [];
    acc[name].push(cb);
    return acc;
  }, {});

  // Phase 3: Group linked filling batches by name
  const groupedFillingBatches = linkedFillingBatches.reduce<Record<string, BatchRecordFillingBatch[]>>((acc, fb) => {
    const name = fb.fillingProductName || 'Unknown';
    if (!acc[name]) acc[name] = [];
    acc[name].push(fb);
    return acc;
  }, {});

  // Phase 4: Group linked dough batches by name
  const groupedDoughBatches = linkedDoughBatches.reduce<Record<string, BatchRecordDoughBatch[]>>((acc, db) => {
    const name = db.doughProductName || 'Unknown';
    if (!acc[name]) acc[name] = [];
    acc[name].push(db);
    return acc;
  }, {});

  const handleOpenAddSubBatch = (targetType: 'componentBatch' | 'fillingBatch' | 'doughBatch', groupName: string) => {
    let available: { id: string; lotNumber: string; date?: string }[] = [];

    if (targetType === 'componentBatch') {
      const linkedIds = new Set(linkedComponentBatches.map(l => l.componentBatchId));
      available = componentBatches
        .filter(cb => cb.productName === groupName && !linkedIds.has(cb.id))
        .map(cb => ({ id: cb.id, lotNumber: cb.batchLotNumber, date: cb.dateOfProduction?.split('T')[0] }));
    }
    if (targetType === 'fillingBatch') {
      const linkedIds = new Set(linkedFillingBatches.map(l => l.fillingBatchId));
      available = fillingBatches
        .filter(fb => fb.productName === groupName && !linkedIds.has(fb.id))
        .map(fb => ({ id: fb.id, lotNumber: fb.batchLotNumber, date: fb.dateOfProduction?.split('T')[0] }));
    }
    if (targetType === 'doughBatch') {
      const linkedIds = new Set(linkedDoughBatches.map(l => l.doughBatchId));
      available = doughBatches
        .filter(db => db.productName === groupName && !linkedIds.has(db.id))
        .map(db => ({ id: db.id, lotNumber: db.batchNumber, date: db.dateOfProduction?.split('T')[0] }));
    }

    setAddSubBatchState({
      isOpen: true,
      targetType,
      groupName,
      availableBatches: available,
      selectedIds: [],
      saving: false,
      error: null
    });
  };

  const handleConfirmAddSubBatch = async () => {
    if (addSubBatchState.selectedIds.length === 0) return;
    setAddSubBatchState(prev => ({ ...prev, saving: true, error: null }));

    try {
      if (addSubBatchState.targetType === 'componentBatch') {
        await Promise.all(addSubBatchState.selectedIds.map(id =>
          fetch('/api/filling-batch-component-batches', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ fillingBatchId: batchId, componentBatchId: id })
          }).then(r => { if (!r.ok) throw new Error(`POST failed (${r.status})`); })
        ));
        // Re-fetch junction data
        const res = await fetch(`/api/filling-batch-component-batches/by-filling-batch/${batchId}`);
        if (res.ok) {
          const data = await res.json();
          setLinkedComponentBatches(data.fillingBatchComponentBatches || []);
        }
      }
      if (addSubBatchState.targetType === 'fillingBatch') {
        await Promise.all(addSubBatchState.selectedIds.map(id =>
          fetch('/api/batch-record-filling-batches', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ batchRecordId: batchId, fillingBatchId: id })
          }).then(r => { if (!r.ok) throw new Error(`POST failed (${r.status})`); })
        ));
        // Re-fetch
        const res = await fetch(`/api/batch-record-filling-batches/by-batch-record/${batchId}`);
        if (res.ok) {
          const data = await res.json();
          setLinkedFillingBatches(data.batchRecordFillingBatches || []);
        }
      }
      if (addSubBatchState.targetType === 'doughBatch') {
        await Promise.all(addSubBatchState.selectedIds.map(id =>
          fetch('/api/batch-record-dough-batches', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ batchRecordId: batchId, doughBatchId: id })
          }).then(r => { if (!r.ok) throw new Error(`POST failed (${r.status})`); })
        ));
        // Re-fetch
        const res = await fetch(`/api/batch-record-dough-batches/by-batch-record/${batchId}`);
        if (res.ok) {
          const data = await res.json();
          setLinkedDoughBatches(data.batchRecordDoughBatches || []);
        }
      }

      setAddSubBatchState({ isOpen: false, targetType: 'componentBatch', groupName: '', availableBatches: [], selectedIds: [], saving: false, error: null });
      onIngredientAdded();
    } catch (err: unknown) {
      setAddSubBatchState(prev => ({
        ...prev,
        saving: false,
        error: err instanceof Error ? err.message : 'Failed to add batch.'
      }));
    }
  };

  if (!isOpen) return null;

  const availableLots = getAvailableLots();

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          width: '90%',
          maxWidth: '800px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '24px'
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', color: '#333' }}>
              {productName}
            </h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#666' }}>
              <span style={{ fontWeight: '600' }}>Lot:</span>{' '}
              <span style={{ fontFamily: 'monospace' }}>{batchLotNumber}</span>
              <span style={{ margin: '0 8px' }}>|</span>
              <span style={{ fontWeight: '600' }}>Date:</span>{' '}
              {dateOfProduction?.split('T')[0] || 'N/A'}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '0',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto'
          }}
        >
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Loading ingredients...
            </div>
          ) : ingredients.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              No ingredients recorded for this batch.
            </div>
          ) : (
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px'
              }}
            >
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th style={tableHeaderStyle}>Ingredient</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Qty</th>
                  <th style={tableHeaderStyle}>Unit</th>
                  <th style={tableHeaderStyle}>Lot Codes</th>
                  <th style={tableHeaderStyle}>Supplier</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'center', width: '80px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {ingredients.map((ingredient) => (
                  <React.Fragment key={ingredient.id}>
                    <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                      <td style={tableCellStyle}>{ingredient.ingredientName}</td>
                      <td style={{ ...tableCellStyle, textAlign: 'right', fontFamily: 'monospace' }}>
                        {ingredient.quantity != null ? ingredient.quantity : '-'}
                      </td>
                      <td style={tableCellStyle}>{ingredient.unit || '-'}</td>
                      <td style={{ ...tableCellStyle, fontFamily: 'monospace' }}>
                        {ingredient.lotNumbers.length > 0
                          ? ingredient.lotNumbers.join(', ')
                          : '-'}
                      </td>
                      <td style={tableCellStyle}>
                        {ingredient.supplierName || '-'}
                      </td>
                      <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                        {batchType !== 'dough' && (
                          <button
                            onClick={() => handleOpenAddLot(ingredient)}
                            disabled={addLotState.isOpen}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: addLotState.isOpen ? '#90CAF9' : '#2196F3',
                              color: 'white',
                              border: 'none',
                              borderRadius: '3px',
                              cursor: addLotState.isOpen ? 'not-allowed' : 'pointer',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}
                          >
                            + Lot
                          </button>
                        )}
                      </td>
                    </tr>
                    {addLotState.isOpen && addLotState.ingredientId === ingredient.id && (
                      <tr>
                        <td colSpan={6} style={{ padding: '12px', backgroundColor: '#f9f9f9', borderBottom: '1px solid #e0e0e0' }}>
                          {addLotState.loading ? (
                            <span style={{ fontSize: '13px', color: '#666' }}>Loading available lots...</span>
                          ) : addLotState.error ? (
                            <span style={{ fontSize: '13px', color: '#c62828' }}>{addLotState.error}</span>
                          ) : addLotState.availableLots.length === 0 ? (
                            <span style={{ fontSize: '13px', color: '#999' }}>No additional lots available for this ingredient.</span>
                          ) : (
                            <div>
                              <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: '#333' }}>
                                Select lots to add to {addLotState.ingredientName}:
                              </div>
                              <div style={{ maxHeight: '150px', overflowY: 'auto', marginBottom: '8px' }}>
                                {addLotState.availableLots.map(lot => (
                                  <label key={lot.lotNumber} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer', padding: '2px 0' }}>
                                    <input
                                      type="checkbox"
                                      checked={addLotState.selectedLots.includes(lot.lotNumber)}
                                      onChange={(e) => {
                                        setAddLotState(prev => ({
                                          ...prev,
                                          selectedLots: e.target.checked
                                            ? [...prev.selectedLots, lot.lotNumber]
                                            : prev.selectedLots.filter(l => l !== lot.lotNumber)
                                        }));
                                      }}
                                    />
                                    <span style={{ fontFamily: 'monospace' }}>{lot.lotNumber}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
                            <button
                              onClick={() => setAddLotState({ isOpen: false, ingredientId: null, ingredientName: '', availableLots: [], selectedLots: [], loading: false, saving: false, error: null })}
                              disabled={addLotState.saving}
                              style={{ padding: '4px 12px', backgroundColor: '#757575', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' }}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleConfirmAddLots}
                              disabled={addLotState.saving || addLotState.selectedLots.length === 0}
                              style={{
                                padding: '4px 12px',
                                backgroundColor: (addLotState.saving || addLotState.selectedLots.length === 0) ? '#A5D6A7' : '#4CAF50',
                                color: 'white', border: 'none', borderRadius: '3px',
                                cursor: (addLotState.saving || addLotState.selectedLots.length === 0) ? 'not-allowed' : 'pointer',
                                fontSize: '12px', fontWeight: '500'
                              }}
                            >
                              {addLotState.saving ? 'Saving...' : 'Confirm'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}

          {/* Component Batches Section (Filling modals) */}
          {batchType === 'filling' && linkedComponentBatches.length > 0 && (
            <div style={{ marginTop: '24px', borderTop: '2px solid #e0e0e0', paddingTop: '16px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#333' }}>Component Batches</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={tableHeaderStyle}>Component</th>
                    <th style={tableHeaderStyle}>Lot Numbers</th>
                    <th style={tableHeaderStyle}>Date</th>
                    <th style={{ ...tableHeaderStyle, textAlign: 'center', width: '80px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupedComponentBatches).map(([name, batches]) => (
                    <React.Fragment key={name}>
                      <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                        <td style={tableCellStyle}>{name}</td>
                        <td style={{ ...tableCellStyle, fontFamily: 'monospace' }}>
                          {batches.map(b => b.batchLotNumber).filter(Boolean).join(', ') || '-'}
                        </td>
                        <td style={tableCellStyle}>
                          {batches[batches.length - 1]?.dateOfProduction?.split('T')[0] || '-'}
                        </td>
                        <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                          <button
                            onClick={() => handleOpenAddSubBatch('componentBatch', name)}
                            disabled={addSubBatchState.isOpen}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: addSubBatchState.isOpen ? '#90CAF9' : '#2196F3',
                              color: 'white', border: 'none', borderRadius: '3px',
                              cursor: addSubBatchState.isOpen ? 'not-allowed' : 'pointer',
                              fontSize: '12px', fontWeight: '500'
                            }}
                          >
                            + Lot
                          </button>
                        </td>
                      </tr>
                      {addSubBatchState.isOpen && addSubBatchState.targetType === 'componentBatch' && addSubBatchState.groupName === name && (
                        <tr>
                          <td colSpan={4} style={{ padding: '12px', backgroundColor: '#f9f9f9', borderBottom: '1px solid #e0e0e0' }}>
                            {addSubBatchState.availableBatches.length === 0 ? (
                              <span style={{ fontSize: '13px', color: '#999' }}>No additional component batches available.</span>
                            ) : (
                              <div>
                                <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: '#333' }}>
                                  Select component batches to link:
                                </div>
                                <div style={{ maxHeight: '150px', overflowY: 'auto', marginBottom: '8px' }}>
                                  {addSubBatchState.availableBatches.map(batch => (
                                    <label key={batch.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer', padding: '2px 0' }}>
                                      <input
                                        type="checkbox"
                                        checked={addSubBatchState.selectedIds.includes(batch.id)}
                                        onChange={(e) => {
                                          setAddSubBatchState(prev => ({
                                            ...prev,
                                            selectedIds: e.target.checked
                                              ? [...prev.selectedIds, batch.id]
                                              : prev.selectedIds.filter(id => id !== batch.id)
                                          }));
                                        }}
                                      />
                                      <span style={{ fontFamily: 'monospace' }}>{batch.lotNumber}</span>
                                      {batch.date && <span style={{ color: '#666' }}>({batch.date})</span>}
                                    </label>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
                              <button
                                onClick={() => setAddSubBatchState(prev => ({ ...prev, isOpen: false }))}
                                disabled={addSubBatchState.saving}
                                style={{ padding: '4px 12px', backgroundColor: '#757575', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' }}
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleConfirmAddSubBatch}
                                disabled={addSubBatchState.saving || addSubBatchState.selectedIds.length === 0}
                                style={{
                                  padding: '4px 12px',
                                  backgroundColor: (addSubBatchState.saving || addSubBatchState.selectedIds.length === 0) ? '#90CAF9' : '#2196F3',
                                  color: 'white', border: 'none', borderRadius: '3px',
                                  cursor: (addSubBatchState.saving || addSubBatchState.selectedIds.length === 0) ? 'not-allowed' : 'pointer',
                                  fontSize: '12px', fontWeight: '500'
                                }}
                              >
                                {addSubBatchState.saving ? 'Saving...' : 'Confirm'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Filling Batches Section (Product modals) */}
          {batchType === 'product' && linkedFillingBatches.length > 0 && (
            <div style={{ marginTop: '24px', borderTop: '2px solid #e0e0e0', paddingTop: '16px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#333' }}>Filling Batches</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={tableHeaderStyle}>Filling</th>
                    <th style={tableHeaderStyle}>Lot Numbers</th>
                    <th style={tableHeaderStyle}>Date</th>
                    <th style={{ ...tableHeaderStyle, textAlign: 'center', width: '80px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupedFillingBatches).map(([name, batches]) => (
                    <React.Fragment key={name}>
                      <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                        <td style={tableCellStyle}>{name}</td>
                        <td style={{ ...tableCellStyle, fontFamily: 'monospace' }}>
                          {batches.map(b => b.fillingBatchLotNumber).filter(Boolean).join(', ') || '-'}
                        </td>
                        <td style={tableCellStyle}>
                          {batches[batches.length - 1]?.fillingDateOfProduction?.split('T')[0] || '-'}
                        </td>
                        <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                          <button
                            onClick={() => handleOpenAddSubBatch('fillingBatch', name)}
                            disabled={addSubBatchState.isOpen}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: addSubBatchState.isOpen ? '#90CAF9' : '#2196F3',
                              color: 'white', border: 'none', borderRadius: '3px',
                              cursor: addSubBatchState.isOpen ? 'not-allowed' : 'pointer',
                              fontSize: '12px', fontWeight: '500'
                            }}
                          >
                            + Lot
                          </button>
                        </td>
                      </tr>
                      {addSubBatchState.isOpen && addSubBatchState.targetType === 'fillingBatch' && addSubBatchState.groupName === name && (
                        <tr>
                          <td colSpan={4} style={{ padding: '12px', backgroundColor: '#f9f9f9', borderBottom: '1px solid #e0e0e0' }}>
                            {addSubBatchState.availableBatches.length === 0 ? (
                              <span style={{ fontSize: '13px', color: '#999' }}>No additional filling batches available.</span>
                            ) : (
                              <div>
                                <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: '#333' }}>
                                  Select filling batches to link:
                                </div>
                                <div style={{ maxHeight: '150px', overflowY: 'auto', marginBottom: '8px' }}>
                                  {addSubBatchState.availableBatches.map(batch => (
                                    <label key={batch.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer', padding: '2px 0' }}>
                                      <input
                                        type="checkbox"
                                        checked={addSubBatchState.selectedIds.includes(batch.id)}
                                        onChange={(e) => {
                                          setAddSubBatchState(prev => ({
                                            ...prev,
                                            selectedIds: e.target.checked
                                              ? [...prev.selectedIds, batch.id]
                                              : prev.selectedIds.filter(id => id !== batch.id)
                                          }));
                                        }}
                                      />
                                      <span style={{ fontFamily: 'monospace' }}>{batch.lotNumber}</span>
                                      {batch.date && <span style={{ color: '#666' }}>({batch.date})</span>}
                                    </label>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
                              <button
                                onClick={() => setAddSubBatchState(prev => ({ ...prev, isOpen: false }))}
                                disabled={addSubBatchState.saving}
                                style={{ padding: '4px 12px', backgroundColor: '#757575', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' }}
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleConfirmAddSubBatch}
                                disabled={addSubBatchState.saving || addSubBatchState.selectedIds.length === 0}
                                style={{
                                  padding: '4px 12px',
                                  backgroundColor: (addSubBatchState.saving || addSubBatchState.selectedIds.length === 0) ? '#90CAF9' : '#2196F3',
                                  color: 'white', border: 'none', borderRadius: '3px',
                                  cursor: (addSubBatchState.saving || addSubBatchState.selectedIds.length === 0) ? 'not-allowed' : 'pointer',
                                  fontSize: '12px', fontWeight: '500'
                                }}
                              >
                                {addSubBatchState.saving ? 'Saving...' : 'Confirm'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Dough Batches Section (Product modals) */}
          {batchType === 'product' && linkedDoughBatches.length > 0 && (
            <div style={{ marginTop: '24px', borderTop: '2px solid #e0e0e0', paddingTop: '16px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#333' }}>Dough Batches</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={tableHeaderStyle}>Dough</th>
                    <th style={tableHeaderStyle}>Batch Numbers</th>
                    <th style={tableHeaderStyle}>Date</th>
                    <th style={{ ...tableHeaderStyle, textAlign: 'center', width: '80px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupedDoughBatches).map(([name, batches]) => (
                    <React.Fragment key={name}>
                      <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                        <td style={tableCellStyle}>{name}</td>
                        <td style={{ ...tableCellStyle, fontFamily: 'monospace' }}>
                          {batches.map(b => b.doughBatchNumber).filter(Boolean).join(', ') || '-'}
                        </td>
                        <td style={tableCellStyle}>
                          {batches[batches.length - 1]?.doughDateOfProduction?.split('T')[0] || '-'}
                        </td>
                        <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                          <button
                            onClick={() => handleOpenAddSubBatch('doughBatch', name)}
                            disabled={addSubBatchState.isOpen}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: addSubBatchState.isOpen ? '#90CAF9' : '#2196F3',
                              color: 'white', border: 'none', borderRadius: '3px',
                              cursor: addSubBatchState.isOpen ? 'not-allowed' : 'pointer',
                              fontSize: '12px', fontWeight: '500'
                            }}
                          >
                            + Lot
                          </button>
                        </td>
                      </tr>
                      {addSubBatchState.isOpen && addSubBatchState.targetType === 'doughBatch' && addSubBatchState.groupName === name && (
                        <tr>
                          <td colSpan={4} style={{ padding: '12px', backgroundColor: '#f9f9f9', borderBottom: '1px solid #e0e0e0' }}>
                            {addSubBatchState.availableBatches.length === 0 ? (
                              <span style={{ fontSize: '13px', color: '#999' }}>No additional dough batches available.</span>
                            ) : (
                              <div>
                                <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: '#333' }}>
                                  Select dough batches to link:
                                </div>
                                <div style={{ maxHeight: '150px', overflowY: 'auto', marginBottom: '8px' }}>
                                  {addSubBatchState.availableBatches.map(batch => (
                                    <label key={batch.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer', padding: '2px 0' }}>
                                      <input
                                        type="checkbox"
                                        checked={addSubBatchState.selectedIds.includes(batch.id)}
                                        onChange={(e) => {
                                          setAddSubBatchState(prev => ({
                                            ...prev,
                                            selectedIds: e.target.checked
                                              ? [...prev.selectedIds, batch.id]
                                              : prev.selectedIds.filter(id => id !== batch.id)
                                          }));
                                        }}
                                      />
                                      <span style={{ fontFamily: 'monospace' }}>{batch.lotNumber}</span>
                                      {batch.date && <span style={{ color: '#666' }}>({batch.date})</span>}
                                    </label>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
                              <button
                                onClick={() => setAddSubBatchState(prev => ({ ...prev, isOpen: false }))}
                                disabled={addSubBatchState.saving}
                                style={{ padding: '4px 12px', backgroundColor: '#757575', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' }}
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleConfirmAddSubBatch}
                                disabled={addSubBatchState.saving || addSubBatchState.selectedIds.length === 0}
                                style={{
                                  padding: '4px 12px',
                                  backgroundColor: (addSubBatchState.saving || addSubBatchState.selectedIds.length === 0) ? '#90CAF9' : '#2196F3',
                                  color: 'white', border: 'none', borderRadius: '3px',
                                  cursor: (addSubBatchState.saving || addSubBatchState.selectedIds.length === 0) ? 'not-allowed' : 'pointer',
                                  fontSize: '12px', fontWeight: '500'
                                }}
                              >
                                {addSubBatchState.saving ? 'Saving...' : 'Confirm'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Add Ingredient Form */}
          {showAddForm && (
            <div
              style={{
                marginTop: '16px',
                padding: '16px',
                backgroundColor: '#f9f9f9',
                borderRadius: '6px',
                border: '1px solid #e0e0e0'
              }}
            >
              <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#333' }}>
                Add Ingredient
              </h4>

              {addError && (
                <div style={{
                  padding: '8px 12px',
                  marginBottom: '12px',
                  backgroundColor: '#ffebee',
                  color: '#c62828',
                  borderRadius: '4px',
                  fontSize: '13px'
                }}>
                  {addError}
                </div>
              )}

              {/* Ingredient Name */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '4px' }}>
                  Ingredient Name
                </label>
                <select
                  value={addIngredientName}
                  onChange={(e) => handleIngredientNameChange(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    fontSize: '14px'
                  }}
                >
                  <option value="">-- Select Ingredient --</option>
                  {getUniqueIngredientNames().map(name => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>

              {/* Lot Numbers */}
              {addIngredientName && (
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '4px' }}>
                    Lot Number
                  </label>
                  {availableLots.length === 0 ? (
                    <p style={{ margin: 0, fontSize: '13px', color: '#999' }}>
                      No lots available for this ingredient.
                    </p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {availableLots.map(lot => (
                        <label key={lot} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="addLotSelection"
                            value={lot}
                            checked={selectedLotNumber === lot}
                            onChange={() => setSelectedLotNumber(lot)}
                          />
                          <span style={{ fontFamily: 'monospace' }}>{lot}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Quantity & Unit */}
              {addIngredientName && (
                <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '4px' }}>
                      Quantity
                    </label>
                    <input
                      type="number"
                      value={addQuantity}
                      onChange={(e) => setAddQuantity(e.target.value)}
                      placeholder="e.g. 500"
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '4px' }}>
                      Unit
                    </label>
                    <select
                      value={addUnit}
                      onChange={(e) => setAddUnit(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        fontSize: '14px'
                      }}
                    >
                      <option value="">-- Select Unit --</option>
                      <option value="g">g</option>
                      <option value="kg">kg</option>
                      <option value="lb">lb</option>
                      <option value="oz">oz</option>
                      <option value="tsp">tsp</option>
                      <option value="tbsp">tbsp</option>
                      <option value="cup">cup</option>
                      <option value="ml">ml</option>
                      <option value="L">L</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Form Actions */}
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setAddIngredientName('');
                    setSelectedLotNumber(null);
                    setAddQuantity('');
                    setAddUnit('');
                    setAddError(null);
                  }}
                  disabled={addLoading}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#757575',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddIngredient}
                  disabled={addLoading || !addIngredientName || !selectedLotNumber}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: addLoading || !addIngredientName || !selectedLotNumber ? '#A5D6A7' : '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: addLoading || !addIngredientName || !selectedLotNumber ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: '500'
                  }}
                >
                  {addLoading ? 'Adding...' : 'Add'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            paddingTop: '16px',
            marginTop: '16px',
            borderTop: '1px solid #e0e0e0'
          }}
        >
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#388E3C';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#4CAF50';
              }}
            >
              {showAddForm ? 'Cancel Add' : '+ Add Ingredient'}
            </button>
            <button
              onClick={onDuplicate}
              style={{
                padding: '10px 20px',
                backgroundColor: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1976D2';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#2196F3';
              }}
            >
              Duplicate Batch
            </button>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: '#757575',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#616161';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#757575';
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const tableHeaderStyle: React.CSSProperties = {
  padding: '12px 8px',
  textAlign: 'left',
  fontWeight: '600',
  color: '#333',
  borderBottom: '2px solid #e0e0e0'
};

const tableCellStyle: React.CSSProperties = {
  padding: '12px 8px',
  color: '#333'
};
